import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Alert, CircularProgress, Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { SubscriptionData } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  sub: SubscriptionData | null;
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function ChangeQuantityModal({ open, onClose, sub, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && sub) {
      setQuantity(sub.quantity || 1);
      setError(null);
    }
  }, [open, sub]);

  const handleSave = async () => {
    if (!sub) return;
    setLoading(true);
    setError(null);

    try {
      const res = await call("/recharge/subscription/update-quantity", {
        method: "POST",
        body: { subscriptionId: sub.id, quantity },
      });
      if (!res.ok) throw new Error(res.error || "Failed to update quantity");
      onSuccess(`Quantity updated to ${quantity}`);
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to update quantity");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800, color: COLORS.brownDeep }}>Change Quantity</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography sx={{ fontSize: 14, color: COLORS.brownDeep, fontWeight: 600, mb: 2 }}>
          {sub?.product_title}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
          <IconButton
            onClick={() => setQuantity(q => Math.max(1, q - 1))}
            disabled={quantity <= 1}
            sx={{ border: `1px solid ${COLORS.borderLight}` }}
          >
            <RemoveIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 800, fontSize: 24, minWidth: 40, textAlign: "center" }}>
            {quantity}
          </Typography>
          <IconButton
            onClick={() => setQuantity(q => Math.min(99, q + 1))}
            disabled={quantity >= 99}
            sx={{ border: `1px solid ${COLORS.borderLight}` }}
          >
            <AddIcon />
          </IconButton>
        </Box>

        <Typography sx={{ fontSize: 12, color: COLORS.textMuted, textAlign: "center", mt: 1 }}>
          Current: {sub?.quantity || 1} &rarr; New: {quantity}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || quantity === (sub?.quantity || 1)}
          sx={{ bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark }, textTransform: "none" }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Update Quantity"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
